import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Configure axios defaults
  useEffect(() => {
    // Determine API URL based on environment
    let apiUrl;
    
    // Check for environment variable first
    if (import.meta.env.VITE_API_URL) {
      apiUrl = import.meta.env.VITE_API_URL;
    } else {
      // Detect if we're in production by checking the hostname
      const isProduction = window.location.hostname.includes('netlify.app') || 
                          window.location.hostname.includes('vercel.app') || 
                          window.location.hostname.includes('railway.app') ||
                          import.meta.env.PROD;
      
      // Use Railway backend for production, localhost for development
      if (isProduction) {
        apiUrl = 'https://predictive-maintenance-v2-production.up.railway.app';
      } else {
        apiUrl = 'http://localhost:5001';
      }
    }
    
    // Ensure API URL has the /api suffix
    const baseURL = apiUrl.endsWith('/api') ? apiUrl : `${apiUrl}/api`;
    
    console.log('Setting API URL:', baseURL);
    console.log('Is Production:', import.meta.env.PROD);
    console.log('Hostname:', window.location.hostname);
    console.log('Environment VITE_API_URL:', import.meta.env.VITE_API_URL);
    
    axios.defaults.baseURL = baseURL;
    axios.defaults.timeout = 30000; // 30 second timeout
    
    // Add request interceptor for better error handling
    axios.interceptors.request.use(
      (config) => {
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for better error handling
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          console.log('Unauthorized access, logging out...');
          logout();
        }
        return Promise.reject(error);
      }
    );
    
  }, [token]);

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get('auth/profile');
          setUser(response.data.user);
        } catch (error) {
          console.error('Auth check failed:', error);
          logout();
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [token]);

  const login = async (username, password) => {
    try {
      console.log('Making login request to:', axios.defaults.baseURL + '/auth/login');
      const response = await axios.post('auth/login', {
        email: username,
        password
      });

      const { token: newToken, user: userData } = response.data;
      
      setToken(newToken);
      setUser(userData);
      localStorage.setItem('token', newToken);

      return { success: true, user: userData };
    } catch (error) {
      console.error('Login failed:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message || 
                          'Login failed - please check your connection';
      return {
        success: false,
        error: errorMessage
      };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
  };

  const updateProfile = async (updates) => {
    try {
      const response = await axios.put('auth/profile', updates);
      setUser(response.data.user);
      return { success: true };
    } catch (error) {
      console.error('Profile update failed:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Profile update failed'
      };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await axios.put('auth/password', {
        currentPassword,
        newPassword
      });
      return { success: true };
    } catch (error) {
      console.error('Password change failed:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Password change failed'
      };
    }
  };

  const hasRole = (role) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return user.role === role;
  };

  const hasAnyRole = (roles) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return roles.includes(user.role);
  };

  const getRoleDisplayName = (role) => {
    const roleNames = {
      admin: 'Administrator',
      operations_manager: 'Operations Manager',
      maintenance_engineer: 'Maintenance Engineer',
      plant_supervisor: 'Plant Supervisor'
    };
    return roleNames[role] || role;
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    updateProfile,
    changePassword,
    hasRole,
    hasAnyRole,
    getRoleDisplayName,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 